import { EMAIL_TOOLBAR_NO_ATTACHMENTS } from '@/components/shared/email/emailToolbar';
import { useMemo } from 'react';
import {
  toSelectOptions,
  toEmailValue,
  fromEmailValue,
} from './surveyEmailValue';
import { EmailComposer } from '@/components/shared/email/EmailComposer';
import type { SurveyEmailUser, SurveyEmailValues } from './surveyEmailValue';
import type { SurveyTemplate } from './useSurveyTemplates';

interface SurveyEmailComposerProps {
  formik?: any;
  fromUsers?: SurveyEmailUser[];
  ccBccUsers?: SurveyEmailUser[];
  // Employee surveys pick their own recipients; candidate surveys have none.
  toUsers?: SurveyEmailUser[];
  variables?: Record<string, string[]>;
  // From/To are only required on the send-survey surface, not the plan editor.
  isRequired?: boolean;
  submitAttempt?: number;
  // The picker lives inside the email card; the surface owns the selection.
  templates?: SurveyTemplate[];
  selectedTemplate?: SurveyTemplate | null;
  onTemplateChange?: (_template: SurveyTemplate | null) => void;
  loadTemplates?: (_input: string) => Promise<SurveyTemplate[]>;
}

/**
 * Survey-email composition on kula-ai `Email`.
 *
 * Replaces `useEmailField`, whose `FormEditor` disables tiptap's `hardBreak`
 * and so drops every `<br>` in a stored template body (ENG-3719).
 */
export const SurveyEmailComposer = ({
  formik,
  fromUsers = [],
  ccBccUsers = [],
  toUsers = [],
  variables,
  isRequired = false,
  submitAttempt = 0,
  templates = [],
  selectedTemplate = null,
  onTemplateChange,
  loadTemplates,
}: SurveyEmailComposerProps) => {
  const toOptions = useMemo(() => toSelectOptions(toUsers), [toUsers]);

  const templateOptions = useMemo(
    () =>
      templates.map((template) => ({
        label: String(template?.name ?? template?.title ?? ''),
        value: String(template?.id),
        data: template,
      })),
    [templates]
  );

  return (
    <EmailComposer
      // The From picker only holds a fetched page of senders; the saved
      // sender may sit outside it (ENG-3766).
      savedUsers={[formik?.values?.fromUserObject].filter(Boolean)}
      formik={formik}
      toValue={(values: any) => toEmailValue(values as SurveyEmailValues)}
      toPatch={fromEmailValue}
      fromUsers={fromUsers}
      ccBccUsers={ccBccUsers}
      valueKey="id"
      toOptions={toOptions}
      templateOptions={templateOptions}
      selectedTemplate={selectedTemplate ?? null}
      onTemplateChange={(templateId) => {
        const picked = templates.find((row) => String(row?.id) === templateId);
        // Deferred: setting parent state inside kula's lifecycle makes Formik
        // flush mid-render.
        queueMicrotask(() => onTemplateChange?.(picked ?? null));
      }}
      {...(loadTemplates
        ? {
            templateSelectProps: {
              variant: 'async' as const,
              defaultOptions: true,
              loadOptions: async (input: string) =>
                (await loadTemplates(input)).map((template) => ({
                  label: String(template?.name ?? template?.title ?? ''),
                  value: String(template?.id),
                  data: template,
                })),
            },
          }
        : {})}
      variables={variables}
      toolbarFeatures={EMAIL_TOOLBAR_NO_ATTACHMENTS}
      submitAttempt={isRequired ? submitAttempt : 0}
    />
  );
};
